import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    OnDestroy,
    OnInit,
    signal, viewChild
} from '@angular/core';
import {filter, finalize, map} from "rxjs/operators";
import {MatPaginator} from "@angular/material/paginator";
import {MatDialog} from "@angular/material/dialog";
import {
    TransactionDetailsDialogComponent
} from "../../dialogs/transaction-details-dialog/transaction-details-dialog.component";
import {Operation} from "../../util/operation";
import {UtilService} from "../../services/util.service";
import {Transaction, TransactionService} from "../../services/transaction.service";
import {BusyService} from "../../services/busy.service";
import {Utils} from "../../util/utils";
import {Constants} from "../../util/constants";
import {MatRadioButton, MatRadioChange, MatRadioGroup} from "@angular/material/radio";
import {Observable, Subscription} from "rxjs";
import {DateTime} from "luxon";
import {NotificationService} from "../../services/notification.service";
import {CacheService} from "../../services/cache.service";
import {CacheMessageMapper, TagInfo, throttleReloads} from "../../util/cache-message-mapper";
import {
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatTable, MatTableDataSource
} from '@angular/material/table';
import {MatButton} from '@angular/material/button';
import {MatOption} from '@angular/material/core';
import {MatSelect} from '@angular/material/select';
import {
    MatDatepickerToggle,
    MatDateRangeInput,
    MatDateRangePicker,
    MatEndDate,
    MatStartDate
} from '@angular/material/datepicker';
import {MatFormField, MatLabel, MatSuffix} from '@angular/material/form-field';
import {User} from "../../services/auth.service";
import {form, FormField, required} from "@angular/forms/signals";

const MIN_WIDTH: number = 320;
const MAX_WIDTH: number = 800;

interface Op {
    id: number;
    description: string;
}

interface TransactionsModel {
    start: Date;
    end: Date;
    user: number;
    operation: number;
}

@Component({
    templateUrl: './transactions.component.html',
    styleUrls: ['./transactions.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatRadioGroup, MatRadioButton, MatFormField, MatLabel, MatDateRangeInput, MatStartDate,
        MatEndDate, MatDatepickerToggle, MatSuffix, MatDateRangePicker, MatSelect, MatOption, MatButton, MatPaginator,
        MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow,
        MatRowDef, MatRow, FormField]
})
export class TransactionsComponent implements OnInit, OnDestroy {

    readonly operations: Op[] = [
        {id: 0, description: 'ALL'},
        {id: Operation.ChangeNameServers, description: "Change nameservers"},
        // {id: Operation.ChangeContactInfo, description: "Change contact info"},
        {id: Operation.Create, description: "Create"},
        {id: Operation.Delete, description: "Delete"},
        // {id: Operation.AvailabilityCheck, description: "Availability check"},
        // {id: Operation.SyncFromRegistry, description: "Sync database from registry"},
        // {id: Operation.SubmitTransfer, description: "Submit Transfer"},
        {id: Operation.Unlock, description: "Unlock"},
        {id: Operation.Lock, description: "Lock"},
        // {id: Operation.CompleteTransfer, description: "Complete Transfer"},
        // {id: Operation.PrivacyInfo, description: "Privacy Info"},
        {id: Operation.ApproveTransfer, description: "Approve Transfer"},
        {id: Operation.SetFlag, description: "Set Flag"},
        {id: Operation.ClearFlag, description: "Clear Flag"},
        // {id: Operation.BulkRenew, description: "Bulk Renew"},
        // {id: Operation.BulkRestore, description: "Bulk Restore"},
        // {id: Operation.BulkRestoreReport, description: "Bulk Restore Report"}
    ];

    public readonly DEFAULT_PAGE_SIZE = 10;
    public readonly MAX_TRANSACTIONS = 20;

    readonly today: Date = new Date();

    /** Format that produces SQL compatible dates: yyyy-mm-dd */
    readonly dateFormat: Intl.DateTimeFormat = new Intl.DateTimeFormat('sv-SE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });

    users = signal<User[]>([]);
    rangeType = signal<'custom' | 'last_n' | 'last_30_days' | 'last_60_days'>('last_n');
    hasSearched = signal(false);
    transactions = signal<Transaction[]>([]);
    paginator = viewChild<MatPaginator>('paginator');
    dataSource = signal(new MatTableDataSource<Transaction>([]));

    private notifSub?: Subscription;
    readonly columnsToDisplay = ['id', 'user', 'op', 'started', 'notes', 'actions'];

    transactionsModel = signal<TransactionsModel>({
        start: this.today,
        end: this.today,
        user: 0,
        operation: 0
    });
    transactionsForm = form(this.transactionsModel, (schemaPath) => {
        required(schemaPath.start, {message: Constants.ERR_REQUIRED});
        required(schemaPath.end, {message: Constants.ERR_REQUIRED});
    });


    constructor(private transactionService: TransactionService,
                private utilService: UtilService,
                private busyService: BusyService,
                private cache: CacheService,
                private notificationService: NotificationService,
                private dialog: MatDialog) {
        // Bind the paginator to the current data source whenever either becomes
        // available. The paginator lives inside a `[hidden]` div, so its
        // viewChild is undefined during the initial busy/empty load — assigning
        // `source.paginator` once inside the data callback (as before) captured
        // a null and left the table unpaginated ("0 of 0"). This effect re-runs
        // when the paginator finally renders and reconnects it.
        effect(() => {
            const paginator = this.paginator();
            const source = this.dataSource();
            if (paginator && source.paginator !== paginator)
                source.paginator = paginator;
        });
    }

    ngOnInit() {
        // Connect to push notification service if not yet connected (e.g. if page reloaded)
        this.notificationService.connect();

        this.busyService.showBusy();
        this.utilService.getUsers().pipe(
            finalize(() => {
                this.busyService.showNotBusy()
            })
        ).subscribe({
            next: (users) => {
                // Insert an ALL entry with id 0 at the top, if not there already
                if (users.length == 0 || users[0].id != 0)
                    users.splice(0, 0, {id: 0, email: 'ALL'})
                this.users.set(users);
                this.loadRangeTransactions(this.getRangeObservable()!);
            },
            error: this.utilService.getErrorHandler("error getting users")
        });
        // Update the transaction list when the server tells us it has changed. Any transaction anywhere
        // sends one, so a long bulk run sends a steady stream: throttle the reloads.
        this.notifSub = this.notificationService.message$.pipe(
            map(message => CacheMessageMapper.map(message, CacheMessageMapper.TRANSACTIONS_PAT)),
            filter((info): info is TagInfo => info !== undefined),
            throttleReloads()
        ).subscribe((info) => {
            console.log("transaction notification received: must reload transactions...");
            this.cache.clear(...info.tags);
            this.transactions.set([]);
            this.reloadTransactions();
        });
    }

    ngOnDestroy() {
        this.notifSub?.unsubscribe();
    }

    isBusy = computed(() => this.busyService.isBusy())

    // transRange = computed(() => {
    //     if (!this.paginator())
    //         return [];
    //     const pageSize = this.paginator()!.pageSize;
    //     const offset = this.paginator()!.pageIndex * pageSize;
    //     return this.transactions().slice(offset, offset + pageSize);
    // });

    formatTimestamp(sd: string): string {
        return sd ? Constants.US_TIMESTAMP_FORMAT.format(new Date(sd)) : "";
    }

    changeRangeType(ev: MatRadioChange) {
        if (ev.value != this.rangeType()) {
            this.hasSearched.set(false);
            this.rangeType.set(ev.value);
            const transactionObs: Observable<Transaction[]> | null = this.getRangeObservable();
            this.transactions.set([]);
            if (transactionObs)
                this.loadRangeTransactions(transactionObs);
        }
    }

    private loadRangeTransactions(transactionObs: Observable<Transaction[]>) {
        const cacheKey = `transactions.${this.rangeType()}`;
        if (this.cache.has(cacheKey)) {
            this.transactions.set(this.cache.get(cacheKey));
            this.updateTransactions(this.transactions());
        } else {
            this.busyService.showBusy();
            transactionObs.pipe(
                finalize(() => {
                    this.busyService.showNotBusy()
                })
            ).subscribe({
                next: (transactions) => {
                    this.updateTransactions(transactions);
                },
                error: this.utilService.getErrorHandler("error getting transactions")
            });
        }
    }

    private updateTransactions(transactions: Transaction[]) {
        this.transactions.set(transactions);
        this.hasSearched.set(true);
        const source = new MatTableDataSource<Transaction>(transactions);
        // Reset to the first page on a fresh load; the paginator is bound to the
        // source by the constructor effect (which also covers the initial load,
        // when the paginator viewChild isn't available yet).
        this.paginator()?.firstPage();
        this.dataSource.set(source);
        this.cache.set(`transactions.${this.rangeType()}`, transactions);
    }

    private getRangeObservable(): Observable<Transaction[]> | null {
        switch (this.rangeType()) {
            case 'last_n':
                return this.transactionService.getAll(this.MAX_TRANSACTIONS);
            case 'last_30_days': {
                const today = DateTime.now().toISODate();
                const start = DateTime.now().minus({days: 30}).toISODate();
                return this.transactionService.search(start, today);
            }
            case 'last_60_days': {
                const today = DateTime.now().toISODate();
                const start = DateTime.now().minus({days: 60}).toISODate();
                return this.transactionService.search(start, today);
            }
            case 'custom':
                return null;
        }
    }

    private reloadTransactions() {
        const transactionObs: Observable<Transaction[]> | null = this.getRangeObservable();
        if (transactionObs)
            this.loadRangeTransactions(transactionObs);
        else
            this.submit();
    }

    showDetail(trans: Transaction) {
        this.dialog.open(TransactionDetailsDialogComponent, {
            minWidth: MIN_WIDTH,
            maxWidth: MAX_WIDTH,
            data: {
                transactionId: trans.transId,
                actions: trans.actions
            }
        });
    }

    userEmail(userId: number): string {
        for (let user of this.users())
            if (user['id'] == userId)
                return Utils.MakeEmailBreakable(user['email']);
        return '?'
    }

    linkDomains(text: string | null | undefined): string {
        // Notes may be null (e.g. historical rows, or ops that record no notes),
        // so guard before calling .replace — otherwise the cell render throws and
        // aborts the whole table (blank/partial render).
        if (!text)
            return "";
        // Find any string looking like *.com, *.net, *.org and turn it into an <a> link to the domains page
        const pat = /\S+\.(?:com|net|org)/g;
        return text.replace(pat, "<a href='/domains/$&'>$&</a>");
    }

    submit(event?: SubmitEvent) {
        event?.preventDefault();
        const start = this.dateFormat.format(this.transactionsForm.start().value());
        const end = this.dateFormat.format(this.transactionsForm.end().value());
        const userId = this.transactionsForm.user().value() || undefined;
        const opId = this.transactionsForm.operation().value() || undefined;
        this.busyService.showBusy();
        this.hasSearched.set(false);
        this.transactionService.search(start, end, userId, opId).pipe(
            finalize(() => {
                this.busyService.showNotBusy();
            })
        ).subscribe({
            next: (transactions) => {
                this.updateTransactions(transactions);
            },
            error: this.utilService.getErrorHandler("error getting transactions")
        });
    }

    // private breakOpName(trans: Transaction): Transaction {
    //     // Break camel-case operation names into multiple words separate by spaces
    //     const name: string = trans.operation;
    //     const pat: RegExp = /[a-z][A-Z]/g;
    //     let match;
    //     let parts = [];
    //     let start = 0;
    //     while (match = pat.exec(name)) {
    //         parts.push(name.substring(start, match.index + 1));
    //         start = match.index + 1;
    //     }
    //     if (parts.length > 0) {
    //         parts.push(name.substring(start));
    //         trans.operation = parts.join(" ");
    //     }
    //     return trans;
    // }
}
